import React from 'react';
import { Select, Radio, Checkbox, Divider } from 'antd';
import { CustomOfferFormData } from '../types';
import FormInput from './FormInput';

interface PricingFormProps {
	formData: CustomOfferFormData;
	updateFormData: (updates: Partial<CustomOfferFormData>) => void;
	getFieldError: (fieldName: string) => string;
	showTrialPeriod: boolean;
	setShowTrialPeriod: (show: boolean) => void;
	setShowUserData: (show: boolean) => void;
}

const PricingForm: React.FC<PricingFormProps> = ({
	formData,
	updateFormData,
	getFieldError,
	showTrialPeriod,
	setShowTrialPeriod,
	setShowUserData,
}) => {
	const isWireTransfer = formData.paymentMethod === 'wire_transfer';

	const handleBillingIntervalChange = (value: string): void => {
		updateFormData({ interval: value as 'month' | 'year' });
	};

	const handleIntervalCountChange = (value: any): void => {
		updateFormData({ intervalCount: value as number });
	};

	const handlePaymentMethodChange = (value: string): void => {
		const newPaymentMethod = value;
		updateFormData({ paymentMethod: newPaymentMethod });

		if (newPaymentMethod !== 'wire_transfer') {
			updateFormData({ generateAccount: false });
			setShowUserData(false);
		}
	};

	const handleCardRequiredChange = (e: any): void => {
		updateFormData({
			cardRequired: e.target.value,
		});
	};

	const handleGenerateLinkChange = (e: any): void => {
		const newGenerateLink = e.target.checked;
		updateFormData({
			generateLink: newGenerateLink,
			saveDefault: newGenerateLink ? formData.saveDefault : false,
		});
	};

	const handleGenerateAccountChange = (e: any): void => {
		updateFormData({ generateAccount: e.target.checked });
		setShowUserData(e.target.checked);
	};

	const handleSaveDefaultChange = (e: any): void => {
		updateFormData({ saveDefault: e.target.checked });
	};

	const handleTrialPeriodChange = (e: any): void => {
		setShowTrialPeriod(e.target.checked);
	};

	const handleTrialPeriodDaysChange = (value: any): void => {
		updateFormData({ trialPeriodDays: value as number });
	};

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">Billing Interval *</label>
					<Select
						value={formData.interval}
						onChange={handleBillingIntervalChange}
						className="w-full"
						style={{ width: '100%' }}
					>
						<Select.Option value="month">Month</Select.Option>
						<Select.Option value="year">Year</Select.Option>
					</Select>
				</div>

				<FormInput
					label="Interval Count"
					value={formData.intervalCount}
					onChange={handleIntervalCountChange}
					type="number"
					required
				/>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
				<Select
					value={formData.paymentMethod}
					onChange={handlePaymentMethodChange}
					className="w-full"
					style={{ width: '100%' }}
				>
					<Select.Option value="stripe">Stripe</Select.Option>
					<Select.Option value="wire_transfer">Wire Transfer</Select.Option>
				</Select>
			</div>

			{isWireTransfer && (
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">Card Requirement</label>
					<Radio.Group
						value={formData.cardRequired}
						onChange={handleCardRequiredChange}
						className="space-y-2"
					>
						<div className="flex items-center">
							<Radio value={true} className="mr-2">
								<span className="text-sm text-gray-900">Required</span>
							</Radio>
						</div>
						<div className="flex items-center">
							<Radio value={false} className="mr-2">
								<span className="text-sm text-gray-900">Optional</span>
							</Radio>
						</div>
					</Radio.Group>
				</div>
			)}

			<Divider orientation="left" className="text-lg font-medium text-gray-900">
				Generate Options *
			</Divider>

			<div className="space-y-3">
				<Checkbox checked={formData.generateLink} onChange={handleGenerateLinkChange}>
					<span className="text-sm text-gray-900">Generate Link</span>
				</Checkbox>

				{isWireTransfer && (
					<Checkbox checked={formData.generateAccount} onChange={handleGenerateAccountChange}>
						<span className="text-sm text-gray-900">Generate Account</span>
					</Checkbox>
				)}
			</div>

			{getFieldError('generateOptions') && (
				<p className="text-red-500 text-sm mt-1">{getFieldError('generateOptions')}</p>
			)}

			{formData.generateLink && (
				<div className="mt-3">
					<Checkbox checked={formData.saveDefault} onChange={handleSaveDefaultChange}>
						<span className="text-sm text-gray-900">Save as Default</span>
					</Checkbox>
				</div>
			)}

			<div className="flex items-center">
				<Checkbox id="trialPeriod" checked={showTrialPeriod} onChange={handleTrialPeriodChange}>
					<span className="text-sm text-gray-900">Include Trial Period</span>
				</Checkbox>
			</div>

			{showTrialPeriod && (
				<FormInput
					label="Trial Period (Days)"
					value={formData.trialPeriodDays || 7}
					onChange={handleTrialPeriodDaysChange}
					type="number"
				/>
			)}
		</div>
	);
};

export default PricingForm;
